/**
 * Typed fetch wrapper. All API calls go through here (never raw fetch in pages).
 * Sends cookies (BFF session) and surfaces a typed ApiError on non-2xx.
 */

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return null as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Fetches a file endpoint and saves it, rather than parsing the body as JSON.
 *
 * A plain link or `window.open` would be simpler, but not for a POST (the batch
 * PDF export sends an id list), and it loses the error path entirely: a failed
 * download would replace or blank a tab instead of surfacing the server's
 * message. Going through fetch keeps failures on the same code path as every
 * other call — an ApiError carrying `.error` from the JSON body.
 *
 * The filename comes from Content-Disposition when the server sent one, since
 * the server is what knows the employee's name and the submission date.
 */
async function download(method: string, path: string, body?: unknown, fallbackName = 'download'): Promise<void> {
  const res = await fetch(path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, msg);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFrom(res.headers.get('Content-Disposition')) || fallbackName;
  a.click();
  // Revoked on the next tick: Safari has historically cancelled the download if
  // the object URL is released synchronously after click().
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Reads the quoted filename out of a Content-Disposition header. */
function filenameFrom(header: string | null): string {
  const m = header?.match(/filename="([^"]+)"/);
  return m ? m[1] : '';
}

export const api = {
  get: <T = any>(path: string) => request<T>('GET', path),
  post: <T = any>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T = any>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T = any>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T = any>(path: string) => request<T>('DELETE', path),
  download: (path: string, fallbackName?: string) => download('GET', path, undefined, fallbackName),
  downloadPost: (path: string, body: unknown, fallbackName?: string) => download('POST', path, body, fallbackName),
};
